//LIBRARIES
import React, { Component } from 'react';
import Auth from 'j-toker';
import { submit } from 'redux-form';
import { connect } from 'react-redux';
import { getTranslatedString } from 'helpers/i18n';
import { localize } from 'react-localize-redux';
import jstz from 'jstz';
import MediaQuery from 'react-responsive';
import { MdWarning as WarningIcon } from 'react-icons/md';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FlatButton from '@material-ui/core/Button';
import withMobileDialog from '@material-ui/core/withMobileDialog';

//HELPERS
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledAddButton,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';

import AntDrawer from 'components/ui/Drawer';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import StatusTag from 'components/ui/StatusTag';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import UserDetailsOverview from './UserDetailsOverview';
import AddUserForm from './AddUserForm';

import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import DrawerBody from 'components/ui/DrawerBody';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { colors } from 'helpers/ColorHelper';
import { normalizePhone, timeFromNow } from 'helpers/Formatter';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';
import { featureEnabled } from 'helpers/ToggleBot';
import { refreshCurrentAgent } from 'helpers/Authentication';

//ACTIONS
import {
  getUsersStats,
  getFilteredUsers,
  putUser,
  postUser,
  deactivateUser,
  reactivateUser,
  sendInvitation,
  sendPasswordReset,
  putPermissions,
  getUser,
  getRoles,
} from 'actions/landlordUserActions';
import { getProperties } from 'actions/landlordPropertiesActions';
import { updateUserAvailability } from 'actions/userTypesActions';

const ADD_USER_FORM_NAME = 'add_user_form';

const statuses = ['confirmed', 'invited', 'deactivated', 'notinvited'];
const adminStatuses = ['is_admin'];
class LandlordUsers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showAddUserDialog: false,
      showUserDrawer: false,
      disabledAddUserSubmit: false,
      disableAddUserSubmit: true,
      disableEditUserSubmit: true,
      filtersActive: false,
      filters: {
        search: '',
        properties: Array(this.props.properties.length).fill(false),
        statuses: Array(statuses.length).fill(false),
        roles: Array(this.props.roles.length).fill(false),
        adminStatuses: Array(adminStatuses.length).fill(false),
      },
      currentTab: 'all',
      sortKey: '',
      sortOrder: '',
      offset: 0,
      timezone: 'America/Toronto',
    };
  }

  translateText = (text) => this.props.translate(text).props.children;

  fetchUsersAndStats = () => {
    this.props.getUsersStats();
    this.fetchUsers();
  };

  componentDidMount = () => {
    this.fetchUsersAndStats();
    this.props.getProperties({ for_multiselect: true });
    this.props.getRoles();
    const timezone = jstz.determine();
    this.setState({
      timezone: timezone.name() || 'America/Toronto',
    });
  };

  toggleDrawer = async (userType, userId) => {
    if (!this.state.showUserDrawer && Auth.user.admin === true && userId) {
      await this.props.getUser(userType, userId);
      this.setState({
        showUserDrawer: true,
      });
      return;
    }

    this.setState({
      showUserDrawer: false,
      disableEditUserSubmit: true,
    });
  };

  // Methods for the dialog
  toggleAddUser = () => {
    this.setState({
      showAddUserDialog: !this.state.showAddUserDialog,
      disableAddUserSubmit: true,
    });
  };

  postNewUser = (formData) => {
    this.setState({ disabledAddUserSubmit: true });
    this.props.postUser(formData).then(() => {
      this.toggleAddUser();
      this.setState(
        {
          disabledAddUserSubmit: false,
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  updateUser = (formData) => {
    this.props.putUser(formData).then(() => {
      this.props.getUser(formData.user_type, formData.user_id);

      if (formData.user_type === 'Agent' && formData.user_id === this.props.currentUser.userId) {
        refreshCurrentAgent();
      }

      this.setState(
        {
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  deactivateUser = async () => {
    await this.props.deactivateUser(this.props.user.user_type, this.props.user.user_id);

    this.setState(
      {
        showConfirmation: false,
        offset: 0,
      },
      () => {
        this.fetchUsers();
      }
    );
    this.toggleDrawer();
  };

  reactivateUser = async () => {
    await this.props.reactivateUser(
      this.props.user.user_type,
      this.props.user.user_id,
      this.props.user.permissions
    );

    this.setState(
      {
        showConfirmation: false,
        offset: 0,
      },
      () => {
        this.fetchUsers();
      }
    );
    this.toggleDrawer();
  };

  sendInvitation = (user_type, user_id) => {
    this.props.sendInvitation(user_type, user_id).then(() => {
      this.setState(
        {
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  sendPasswordReset = (user_type, user_id) => {
    this.props.sendPasswordReset(user_type, user_id).then(() => {
      this.setState(
        {
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  updateAvailability = (formData) => {
    const { user } = this.props;
    this.props.updateUserAvailability(user.user_type, user.user_id, formData).then(() => {
      this.setState(
        {
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  updatePermissions = (formData) => {
    const { user } = this.props;
    this.props.putPermissions(user.user_type, user.user_id, formData).then(() => {
      this.setState(
        {
          offset: 0,
        },
        () => {
          this.fetchUsers();
        }
      );
    });
  };

  filterProperties = (booleanArrayMask) => {
    return this.props.properties.filter((_, i) => booleanArrayMask[i]);
  };

  filterStatuses = (booleanArrayMask) => {
    return statuses.filter((_, i) => booleanArrayMask[i]);
  };

  filterRoles = (booleanArrayMask) => {
    return this.props.roles.filter((_, i) => booleanArrayMask[i]);
  };

  filterAdminStatuses = (booleanArrayMask) => {
    return adminStatuses.filter((_, i) => booleanArrayMask[i]);
  };

  handleClearSearch = (setFieldValue) => {
    this.setState(
      {
        filtersActive: false,
        filters: {
          search: '',
          properties: Array(this.props.properties.length).fill(false),
          statuses: Array(statuses.length).fill(false),
          roles: Array(this.props.roles.length).fill(false),
          adminStatuses: Array(adminStatuses.length).fill(false),
        },
        offset: 0,
      },
      () => this.fetchUsers()
    );

    if (this.table) this.table.scrollToTop(0);
    setFieldValue('search', '');
    setFieldValue('properties', Array(this.props.properties.length).fill(false));
    setFieldValue('statuses', Array(statuses.length).fill(false));
    setFieldValue('roles', Array(this.props.roles.length).fill(false));
    setFieldValue('adminStatuses', Array(adminStatuses.length).fill(false));
  };

  handleFilters = (values) => {
    const isFiltersUntouched =
      values.search === '' &&
      !values.properties.includes(true) &&
      !values.statuses.includes(true) &&
      !values.roles.includes(true) &&
      !values.adminStatuses.includes(true);

    const newState = {
      filtersActive: !isFiltersUntouched,
      filters: {
        search: values.search,
        properties: values.properties,
        statuses: values.statuses,
        roles: values.roles,
        adminStatuses: values.adminStatuses,
      },
      drawerVisible: false,
      offset: 0,
    };

    if (this.table) this.table.scrollToTop(0);
    this.setState(newState, () => this.fetchUsers());
  };

  columns = [
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.status'),
      dataKey: 'status',
      sortable: false,
      width: 150,
      render: ({ data }) => <StatusTag status={data} />,
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.name'),
      dataKey: 'full_name',
      sortable: true,
      width: 250,
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.email'),
      dataKey: 'email',
      sortable: true,
      width: 250,
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.admin'),
      dataKey: 'admin',
      sortable: false,
      width: 100,
      render: ({ data }) => {
        return data ? getTranslatedString('landlordPortal.users.tableColumns.true') : '-';
      },
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.userType'),
      dataKey: 'user_type',
      sortable: false,
      width: 165,
      render: ({ data }) => getTranslatedString(`landlordPortal.users.apiTypes.${data}`),
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.role'),
      dataKey: 'role_name',
      sortable: false,
      width: 150,
      render: ({ data }) =>
        data === 'Custom' ? getTranslatedString('landlordPortal.users.roleName.custom') : data,
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.calendarSync'),
      dataKey: 'calendar_synced',
      sortable: false,
      width: 150,
      render: ({ data }) =>
        data === 'None' ? getTranslatedString('landlordPortal.users.calendarSync.none') : data,
    },
    {
      title: getTranslatedString('landlordPortal.users.tableColumns.lastActive'),
      dataKey: 'last_active',
      sortable: true,
      width: 200,
      render: ({ data }) =>
        timeFromNow(data, {
          language: this.props.currentLanguage,
          timezone: this.state.timezone,
        }) || '-',
    },
  ];

  handleTabChange = (tabName, _queryObj) => {
    this.setState(
      {
        currentTab: tabName,
        offset: 0,
      },
      () => {
        this.fetchUsers();
      }
    );
    if (this.table) this.table.scrollToTop(0);
  };

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchUsers();
      }
    );
    if (this.table) this.table.scrollToTop(0);
  };

  setRef = (ref) => (this.table = ref);

  fetchUsers = () => {
    const { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

    let fetchPromise = null;

    let filterOptions = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    if (filtersActive) {
      const propertiesToFilter = this.filterProperties(filters.properties).map(
        (data) => data.value
      );
      const statusesToFilter = this.filterStatuses(filters.statuses);
      const rolesToFilter = this.filterRoles(filters.roles).map((data) => data.value);
      const adminStatusesToFilter = this.filterAdminStatuses(filters.adminStatuses);

      filterOptions = {
        query: filters.search,
        property_ids: propertiesToFilter,
        statuses: statusesToFilter,
        role_ids: rolesToFilter,
        is_admin: adminStatusesToFilter,
        sort_key: sortKey,
        sort_order: sortOrder,
      };
      fetchPromise = this.props.getFilteredUsers('all', offset, filterOptions);
    } else {
      fetchPromise = this.props.getFilteredUsers(currentTab, offset, filterOptions);
    }

    fetchPromise.then(() => {
      this.setState({
        offset: this.props.users.length,
      });
    });
  };

  _renderAddNewButton = () => {
    if (featureEnabled('hide-user-creation')) return null;

    if (!Auth.user.admin) return null;

    return (
      <StyledAddButton accentColor={this.accentColor} onClick={this.toggleAddUser}>
        {getTranslatedString('landlordPortal.users.table.addNew')}
      </StyledAddButton>
    );
  };

  accentColor = AccentColor();

  render() {
    const { filters } = this.state;
    const { users, usersCounts, searchTotalResultsCount } = this.props;

    const filterOptions = [
      {
        useSearch: true,
        options: this.props.properties.map((data) => data.label),
        name: 'properties',
        title: getTranslatedString('landlordPortal.users.table.selectProperties'),
      },
      {
        useSearch: false,
        options: statuses.map((statusCode) =>
          getTranslatedString(`landlordPortal.users.statuses.${statusCode}`)
        ),
        name: 'statuses',
        title: getTranslatedString('landlordPortal.users.table.filterBy'),
      },
      {
        useSearch: false,
        options: adminStatuses.map((statusCode) =>
          getTranslatedString(`landlordPortal.users.statuses.${statusCode}`)
        ),
        name: 'adminStatuses',
        title: getTranslatedString('landlordPortal.users.table.filterByIsAdmin'),
      },
      {
        useSearch: true,
        options: this.props.roles.map((data) => data.label),
        name: 'roles',
        title: getTranslatedString('landlordPortal.users.table.filterByRoles'),
      },
    ];

    const agentTabs = [
      {
        title: getTranslatedString('landlordPortal.users.table.all'),
        label: getTranslatedString('landlordPortal.users.table.all'),
        value: 'all',
        count: usersCounts['all'] || 0,
        onClick: () => this.handleTabChange('all', { subset: 'all' }),
      },
      {
        title: getTranslatedString('landlordPortal.users.table.agents'),
        label: getTranslatedString('landlordPortal.users.table.agents'),
        value: 'agents',
        count: usersCounts['agents'] || 0,
        onClick: () => this.handleTabChange('agents', { subset: 'agents' }),
      },
      {
        title: getTranslatedString('landlordPortal.users.table.maintenance'),
        label: getTranslatedString('landlordPortal.users.table.maintenance'),
        value: 'maintenances',
        count: usersCounts['maintenances'] || 0,
        onClick: () => this.handleTabChange('maintenances', { subset: 'maintenances' }),
      },
      {
        title: getTranslatedString('landlordPortal.users.table.inactive'),
        label: getTranslatedString('landlordPortal.users.table.inactive'),
        value: 'inactive',
        count: usersCounts['inactive'] || 0,
        onClick: () => this.handleTabChange('inactive', { subset: 'inactive' }),
      },
    ];

    const maintenanceTabs = [
      {
        title: getTranslatedString('landlordPortal.users.table.all'),
        label: getTranslatedString('landlordPortal.users.table.all'),
        value: 'all',
        count: usersCounts['all'] || 0,
        onClick: () => this.handleTabChange('all', { subset: 'all' }),
      },
      {
        title: getTranslatedString('landlordPortal.users.table.inactive'),
        label: getTranslatedString('landlordPortal.users.table.inactive'),
        value: 'inactive',
        count: usersCounts['inactive'] || 0,
        onClick: () => this.handleTabChange('inactive', { subset: 'inactive' }),
      },
    ];

    const tabs =
      this.props.currentUserType === 'maintenance' || !featureEnabled('ui.users.maintenance_users')
        ? maintenanceTabs
        : agentTabs;

    const filteredData = users.map((datum) => {
      if (datum.phone_number) {
        return {
          ...datum,
          phone_number: normalizePhone(datum.phone_number),
        };
      } else {
        return datum;
      }
    });
    return (
      <StyledMain>
        {Auth.user &&
          Auth.user.type &&
          Auth.user.type.toLowerCase() === 'maintenance' &&
          Auth.user.admin === true && (
            <div style={{ float: 'right', padding: '5px' }}>
              <a href={'/maintenance/dashboard'}>&larr; Go Back</a>
            </div>
          )}
        <StyledHeaderContainer>
          <StyledHeader>
            <HeaderWithPeriod
              headerText={getTranslatedString('landlordPortal.users.users')}
              newStyle
            />
            {this._renderAddNewButton()}
          </StyledHeader>

          <TableFilters
            filters={filters}
            handleSubmitFilters={this.handleFilters}
            handleClearFilters={this.handleClearSearch}
            filterOptions={filterOptions}
            accentColor={this.accentColor}
          />
        </StyledHeaderContainer>

        <StyledTableContainer>
          {this._renderTableView({ filteredData, tabs, searchTotalResultsCount })}
          {this._renderMobileView({
            filteredData,
            tabs,
            currentTab: this.state.currentTab,
            tabChange: this.handleTabChange,
          })}
        </StyledTableContainer>
        <RenderIfToggleOff toggle="ui.users.new_create_edit_drawer">
          {this._renderAddUserDialog()}
        </RenderIfToggleOff>
        <RenderIfToggleOn toggle="ui.users.new_create_edit_drawer">
          {this._renderAddUserDrawer()}
        </RenderIfToggleOn>
        {this._renderUserDrawer()}
      </StyledMain>
    );
  }

  renderGoBackButton = () => {
    if (this.props.currentUserType !== 'maintenance') return null;

    return (
      <div>
        <a href={'/maintenance/dashboard'}>&larr; Go Back</a>
      </div>
    );
  };

  _renderAddUserDrawer = () => (
    <AntDrawer
      visible={this.state.showAddUserDialog}
      onClose={this.toggleAddUser}
      width={550}
      closable={false}
    >
      <DrawerHeader
        onClose={() => {
          this.toggleAddUser();
        }}
        title={getTranslatedString('landlordPortal.users.addUserDialog.title')}
        closeVerbiage={getTranslatedString('landlordPortal.applicantDialog.cancel')}
      />
      <DrawerBody>
        {this.state.showAddUserDialog && (
          <AddUserForm
            formName={ADD_USER_FORM_NAME}
            enableSubmit={() => this.setState({ disableAddUserSubmit: false })}
            postNewUser={this.postNewUser}
            properties={this.props.properties}
            initialValues={{
              show_availability: true,
              can_manually_assign: true,
              can_only_send_by_notice_template: true,
              preference_attributes: { maintenance_notifications: true },
            }}
            currentUserType={this.props.currentUserType}
          />
        )}
      </DrawerBody>
      <DrawerFooter
        primaryActionFormName={ADD_USER_FORM_NAME}
        primaryActionText={getTranslatedString('landlordPortal.users.addUserDialog.save')}
        primaryActionDisabled={this.state.disableAddUserSubmit}
      />
    </AntDrawer>
  );

  _renderAddUserDialog = () => {
    return (
      <Dialog
        disableBackdropClick
        fullScreen={this.props.fullScreen}
        open={this.state.showAddUserDialog}
        onClose={this.toggleAddUser}
        aria-labelledby="add-new-user"
        classes={{ paper: 'form-dialog' }}
      >
        <DialogTitle id="add-new-user">
          <h3 style={{ textAlign: 'center', margin: 0 }}>
            {getTranslatedString('landlordPortal.users.addUserDialog.title')}
          </h3>
        </DialogTitle>
        <DialogContent>
          <AddUserForm
            formName={ADD_USER_FORM_NAME}
            postNewUser={this.postNewUser}
            properties={this.props.properties}
            initialValues={{
              show_availability: true,
              can_manually_assign: true,
              can_only_send_by_notice_template: true,
              preference_attributes: { maintenance_notifications: true },
            }}
            currentUserType={this.props.currentUserType}
          />
        </DialogContent>
        <DialogActions style={{ display: 'block' }}>
          <FlatButton
            onClick={this.toggleAddUser}
            id="close-button"
            style={{ color: AccentColor() }}
          >
            {getTranslatedString('landlordPortal.users.addUserDialog.cancel')}
          </FlatButton>
          <FlatButton
            variant="raised"
            onClick={() => this.props.submit(ADD_USER_FORM_NAME)}
            disabled={this.state.disabledAddUserSubmit}
            id="submit-button"
            style={{
              color: 'white',
              backgroundColor: this.state.disabledSubmit ? colors.LIGHT_GRAY : AccentColor(),
            }}
          >
            {getTranslatedString('landlordPortal.users.addUserDialog.save')}
          </FlatButton>
        </DialogActions>
      </Dialog>
    );
  };

  _renderMobileView = ({ filteredData, tabs, currentTab, tabChange }) => (
    <MediaQuery query="(max-device-width: 750px)">
      <MobileTable
        data={filteredData}
        tabs={tabs}
        tabIndex={currentTab}
        tabChange={tabChange}
        filtersActive={this.state.filtersActive}
        columns={this.columns}
        title="full_name"
        tag="status"
        stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
        fieldsBelowFold={[
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.email'),
            key: 'email',
          },
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.admin'),
            key: 'admin',
            dataType: 'boolean',
          },
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.role'),
            key: 'role',
          },
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.calendarSync'),
            key: 'calendar_synced',
          },
        ]}
        fieldsAboveFold={[
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.userType'),
            key: 'user_type',
          },
          {
            title: getTranslatedString('landlordPortal.users.tableColumns.lastActive'),
            key: 'last_active',
          },
        ]}
        onRowClick={(data) => {
          this.toggleDrawer(data.user_type, data.user_id);
        }}
      />
    </MediaQuery>
  );

  _renderTableView = ({ filteredData, tabs, searchTotalResultsCount }) => (
    <MediaQuery query="(min-device-width: 750px)">
      <TabbedTable
        setRef={this.setRef}
        tabs={tabs}
        columns={this.getEnhancedColumns()}
        data={filteredData}
        loadMoreData={this.fetchUsers}
        isLoading={this.props.fetchingUsers}
        filtersActive={this.state.filtersActive}
        onRowClick={(data) => {
          this.toggleDrawer(data.user_type, data.user_id);
        }}
        onColumnClick={(data) => {
          this.handleSort(data.key, data.order);
        }}
        searchTotalResultsCount={searchTotalResultsCount}
      />
    </MediaQuery>
  );

  getEnhancedColumns = () => {
    if (featureEnabled('bounce_emails')) {
      return [
        {
          title: getTranslatedString('landlordPortal.users.tableColumns.attentionRequired'),
          dataKey: 'attention_required',
          sortable: false,
          width: 140,
          render: ({ data }) => {
            if (!data) return null;
            return (
              <div style={{ textAlign: 'center' }}>
                <WarningIcon
                  size={14}
                  style={{ verticalAlign: 'text-bottom', color: colors.PUMPKIN_ORANGE }}
                />
              </div>
            );
          },
        },
      ].concat(this.columns);
    }

    return this.columns;
  };

  _renderUserDrawer = () => {
    return (
      <AntDrawer
        visible={this.state.showUserDrawer}
        onClose={this.toggleDrawer}
        width={550}
        closable={false}
      >
        {this.state.showUserDrawer && (
          <UserDetailsOverview
            enableSubmit={() => this.setState({ disableEditUserSubmit: false })}
            disableEditUserSubmit={this.state.disableEditUserSubmit}
            toggleDrawer={this.toggleDrawer}
            user={this.props.user}
            updateUser={this.updateUser}
            deactivateUser={this.deactivateUser}
            reactivateUser={this.reactivateUser}
            properties={this.props.properties}
            sendInvitation={this.sendInvitation}
            sendPasswordReset={this.sendPasswordReset}
            updateAvailability={this.updateAvailability}
            updatePermissions={this.updatePermissions}
          />
        )}
      </AntDrawer>
    );
  };
}

const ConnectedLandlordUsers = connect(
  (store) => ({
    users: store.landlordUsers.users,
    usersCounts: store.landlordUsers.usersCounts,
    searchTotalResultsCount: store.landlordUsers.searchTotalResultsCount,
    user: store.landlordUsers.user,
    roles: store.landlordUsers.roles,
    fetchingUsers: store.landlordUsers.fetchingUsers,
    properties: store.landlordProperties.properties,
    currentUserType: store.userTypes.userType,
    currentUser: store.userTypes,
    currentLanguage: store.locale.currentLanguage,
  }),
  {
    getUsersStats,
    getFilteredUsers,
    putUser,
    postUser,
    deactivateUser,
    reactivateUser,
    sendInvitation,
    sendPasswordReset,
    submit,
    updateUserAvailability,
    putPermissions,
    getProperties,
    getUser,
    getRoles,
  }
)(LandlordUsers);

export default localize()(withMobileDialog()(ConnectedLandlordUsers));
